import { DateInputField, DateInputFieldType } from '@tableau/widgets';
import React from 'react';

import { TypeUnsafeTranslation } from '~src/common/react/IReactTranslateService';
import { classes, cssClass } from '~src/style';

import { ClearFilterButton } from './ClearFilterButton';
import { IFilterControl, submitUrlQuery } from './FilterPanel';
import { filterComponent, filterWrapper } from './FilterPanelStyles';

export interface DatePickerFilterProps {
  readonly filterControl: IFilterControl;
  readonly shouldClear: boolean;
}

// Overriding the DateInput to look like DropdownButton rather than a input field
const dateInputDropdownStyleOverrides = cssClass('date-picker-filter', 'field-wrapper', {
  $nest: {
    '& input': {
      cursor: 'default',
      userSelect: 'none',
      '-webkit-user-select': 'none'
    }
  }
});

export function DatePickerFilter(props: DatePickerFilterProps) {
  const { filterControl } = props;

  const ref = React.useRef<DateInputField>();
  const inputRef = React.useRef<HTMLInputElement>();
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>((filterControl.value as Date) || undefined);

  const handleConfirm = (selectedDate: Date) => {
    setSelectedDate(selectedDate);

    const params = filterControl.definition.valueToParams(selectedDate);
    submitUrlQuery(params);
  };

  const clearFilter = () => {
    handleConfirm(undefined);
  };

  // Needed for updates from the URL
  React.useEffect(
    () => {
      const updatedValue = filterControl.value;
      if (selectedDate !== updatedValue) {
        setSelectedDate(updatedValue || undefined);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterControl.value]
  );

  // Listen for clearing all filters
  React.useEffect(
    () => {
      if (props.shouldClear) {
        setSelectedDate(undefined);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.shouldClear]
  );

  /** ensure that clicking the <input> doesn't re-trigger the dropdown popup */
  const handleMouseDown: React.MouseEventHandler<HTMLElement> = e => {
    if (e.target === inputRef.current) {
      ref.current.toggleDatePicker(inputRef.current.getAttribute('aria-expanded') !== 'true');
    }
  };

  /** the DateInputField only opens the dropdown on SPACE when the text is selected -- instead make it *always* open the dropdown regardless */
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === ' ') {
      ref.current.toggleDatePicker(true, true);
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /** prevent the browser-default behavior that sometimes auto-selects the text, despite `user-select: none` (seems like a browser bug) */
  const handleFocus: React.FocusEventHandler<HTMLInputElement> = e => e.currentTarget.setSelectionRange(0, 0);

  /**
   * Make the DateInputField + DatePicker act like a DropdownButton + DatePicker. Don't allow user to input
   * a specific date in the textfield. Instead, date filters are selected only through the DatePicker. This
   * mimics the behavior of the pre-ARM conversion filter panel. Widgets team is discussing exposing this paradigm.
   */
  const dropdownBehaviorProps = {
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    onFocus: handleFocus,
    readOnly: true
  };

  return <div className={filterWrapper}>
    <div className={classes(filterComponent, dateInputDropdownStyleOverrides)}>
      <DateInputField
        dateInputFieldType={DateInputFieldType.Line}
        hasDatePicker={true}
        inputRef={inputRef}
        label={TypeUnsafeTranslation.translate(filterControl.definition.name)}
        onConfirm={handleConfirm}
        ref={ref}
        selectedDate={selectedDate}
        testId={'date-picker-filter-' + filterControl.definition.name}
        {...dropdownBehaviorProps}
      />
    </div>
    {selectedDate &&
      <ClearFilterButton
        clearFilter={clearFilter}
        filterName={filterControl.definition.name}
        getElementToRefocus={() => inputRef.current}
      />
    }
  </div>;
}
